/**
 * Reusable role permission utilities for frontend
 * Centralizes role-based permission checks to prevent code duplication
 */

import {
  INDIVIDUAL_ROLE_ID,
  NCG_ROLE_ID,
  RFP_ROLE_ID,
  SECRETARIAT_ROLE_ID,
  BRC_ROLE_ID,
  BIRDLIFE_SECRETARIAT_ROLE_ID
} from 'constants/roles'

/**
 * Roles that can create new sites or initiate reassessments
 */
export const CAN_CREATE_SITES_ROLES = [INDIVIDUAL_ROLE_ID, NCG_ROLE_ID]

/**
 * Roles that can view and manage users
 */
export const CAN_MANAGE_USERS_ROLES = [SECRETARIAT_ROLE_ID, RFP_ROLE_ID, BRC_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID]

/**
 * Roles that can review and approve/reject proposed sites
 */
export const CAN_REVIEW_SITES_ROLES = [RFP_ROLE_ID, BRC_ROLE_ID]

/**
 * Roles that can confirm sites (final approval)
 * Note: BirdLife Secretariat can only confirm IBA sites (checked separately)
 */
export const CAN_CONFIRM_SITES_ROLES = [SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID]

/**
 * Roles that can view sensitive data and all statuses in their region
 */
export const CAN_VIEW_SENSITIVE_ROLES = [SECRETARIAT_ROLE_ID, RFP_ROLE_ID, NCG_ROLE_ID, BRC_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID]

/**
 * Roles that cannot update sites data
 */
export const CAN_NOT_VIEW_UPDATE_SITES_DATA_ROLES = [SECRETARIAT_ROLE_ID, RFP_ROLE_ID, BRC_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID]

/**
 * Roles that are secretariat-type (can confirm sites, manage users, etc.)
 */
export const SECRETARIAT_TYPE_ROLES = [SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID]

/**
 * Roles that use CountriesOfInterest for regional filtering (RFP and BRC)
 */
export const REGIONAL_COORDINATOR_ROLES = [RFP_ROLE_ID, BRC_ROLE_ID]

/**
 * Check if a role can create sites
 */
export const canCreateSites = (roleId) => {
  return CAN_CREATE_SITES_ROLES.includes(roleId)
}

/**
 * Check if a role can manage users
 */
export const canManageUsers = (roleId) => {
  return CAN_MANAGE_USERS_ROLES.includes(roleId)
}

/**
 * Check if a role can review sites
 */
export const canReviewSites = (roleId) => {
  return CAN_REVIEW_SITES_ROLES.includes(roleId)
}

/**
 * Check if a role can confirm sites
 */
export const canConfirmSites = (roleId) => {
  return CAN_CONFIRM_SITES_ROLES.includes(roleId)
}

/**
 * Check if a role can view sensitive data
 */
export const canViewSensitive = (roleId) => {
  return CAN_VIEW_SENSITIVE_ROLES.includes(roleId)
}

/**
 * Check if the current user can view sensitive species at this site.
 * True when: user has a role that can view sensitive (RFP, Secretariat, etc.)
 * OR the site/siteHistory has been approved for sensitive species visibility for this proposer/co-proposer.
 * Backend sets site.sensitiveSpeciesVisibilityApproved (or SitesHistory.sensitiveSpeciesVisibilityApproved)
 * when the current user is the proposer or a co-proposer and KBA Sec/RFP has approved visibility for this site.
 */
export const canViewSensitiveSpeciesAtSite = (site, roleId) => {
  if (canViewSensitive(roleId)) {
    return true
  }
  const approved =
    site?.sensitiveSpeciesVisibilityApproved === true ||
    site?.sensitive_species_visibility_approved === true ||
    site?.SitesHistory?.sensitiveSpeciesVisibilityApproved === true ||
    site?.SitesHistory?.sensitive_species_visibility_approved === true
  return Boolean(approved)
}

/**
 * Check if a role is a regional coordinator (uses CountriesOfInterest)
 */
export const isRegionalCoordinator = (roleId) => {
  return REGIONAL_COORDINATOR_ROLES.includes(roleId)
}

/**
 * Check if a role cannot update sites data
 */
export const canNotViewUpdateSitesData = (roleId) => {
  return CAN_NOT_VIEW_UPDATE_SITES_DATA_ROLES.includes(roleId)
}

/**
 * Check if a role is a secretariat-type role
 */
export const isSecretariatType = (roleId) => {
  return SECRETARIAT_TYPE_ROLES.includes(roleId)
}

/**
 * Check if a role is BirdLife Secretariat
 */
export const isBirdLifeSecretariat = (roleId) => {
  return roleId === BIRDLIFE_SECRETARIAT_ROLE_ID
}

